import { useCallback, useEffect, useState } from 'react';
import { stateSetter } from '@/utils';
import { IServerColumnConfig } from '@/components/table/column-config';
import { FeedbackReturnType, FeedbackType, IFeedback } from './types';
import {
	deleteFeedback,
	fetchFeedback,
	fetchFeedbackQuery,
	fetchFeedbackTableConfig,
	fetchSingleFeedback,
	patchFeedback
} from './actions';

// Fallback stabil ca identitate pentru `data` neîncărcat — un `[]` nou la fiecare
// randare re-declanșează în lanț efectele consumatorilor cu dependență pe `data`
// („Maximum update depth exceeded").
const EMPTY_LIST: never[] = [];

export function useFeedback<T extends IFeedback | IFeedback[]>(
	typeParam: FeedbackType | undefined = undefined,
	parsId: number | undefined = undefined,
	queryParam: string | undefined = undefined,
	/**
	 * Paginarea INIȚIALĂ (de regulă cea din URL, via useListQueryState). Fără ea,
	 * starea internă pornește pe 0/10 și primul fetch pleacă cu valori greșite, urmat
	 * de un al doilea după sincronizarea din efectele-punte — două call-uri și un race
	 * pe răspunsuri. Doar valoarea de la primul render contează.
	 */
	initialPagination: { page: number; items: number } | undefined = undefined,
	withTableConfig = false
): FeedbackReturnType<T> {
	const [data, setData] = useState<T | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [loadingCTA, setLoadingCTA] = useState<boolean>(false);
	// Configurația de coloane (api/feedbacks/table-config) — cerută doar de pagina de
	// listă (withTableConfig); la eroare rămâne null și tabelul cade silențios pe
	// coloanele definite local.
	const [tableConfig, setTableConfig] = useState<IServerColumnConfig[] | null>(null);
	const [loadingTableConfig, setLoadingTableConfig] = useState<boolean>(
		Boolean(withTableConfig)
	);
	const [page, setPage] = useState<number>(initialPagination?.page ?? 0);
	const [items, setItems] = useState<number>(initialPagination?.items ?? 10);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [totalItems, setTotalItems] = useState<number>(1);

	useEffect(() => {
		if (!withTableConfig) return undefined;
		let active = true;
		setLoadingTableConfig(true);
		fetchFeedbackTableConfig()
			.then((columns) => {
				if (active) setTableConfig(columns.length ? columns : null);
			})
			.catch(() => {
				// Fallback silențios: fără config BE, tabelul folosește coloanele locale.
				if (active) setTableConfig(null);
			})
			.finally(() => {
				if (active) setLoadingTableConfig(false);
			});
		return () => {
			active = false;
		};
	}, [withTableConfig]);

	const handleResponse = (pageParam: number, itemsParam: number): void => {
		setTotalPages(pageParam);
		setTotalItems(itemsParam);
	};

	const getFeedbacks = useCallback(
		async (type: FeedbackType, pageParam: number, itemsParam: number) => {
			try {
				setLoading(true);
				const response = await fetchFeedback(type, pageParam, itemsParam, handleResponse);
				setData(response as T);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoading(false);
			}
		},
		 
		[setData]
	);

	const getVersionsQuery = useCallback(
		async (query: string, queryPage: number, queryItems: number) => {
			try {
				setLoading(true);
				const response = await fetchFeedbackQuery(
					query,
					queryPage,
					queryItems,
					handleResponse
				);
				setData(response as T);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoading(false);
				setLoadingCTA(false);
			}
		},
		 
		[setData]
	);

	const getSingleFeedback = useCallback(
		async (id: number) => {
			try {
				setLoading(true);
				const response = await fetchSingleFeedback(id);
				setData(response as T);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoading(false);
			}
		},
		 
		[setData]
	);

	const removeFeedback = useCallback(
		async (feedbackId: number, onSuccess?: () => void) => {
			try {
				setLoadingCTA(true);
				const response = await deleteFeedback(feedbackId);
				if (response === 200) {
					const temp = Array.isArray(data)
						? data.filter((feedback) => feedback.id !== feedbackId)
						: data;
					setData(temp as T);
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingCTA(false);
			}
		},
		 
		[setData, data]
	);

	const editFeedback = useCallback(
		async (id: number, statusId: number, onSuccess?: () => void) => {
			try {
				setLoadingCTA(true);
				const response = await patchFeedback(id, statusId);
				if (response === 200) {
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingCTA(false);
			}
		},
		 
		[setData]
	);

	useEffect(() => {
		if (typeParam && queryParam === undefined) {
			getFeedbacks(typeParam, page, items);
		}
		 
	}, [typeParam, queryParam, page, items]);

	useEffect(() => {
		if (parsId) {
			getSingleFeedback(parsId);
		}
		 
	}, [parsId]);

	useEffect(() => {
		if (typeParam && queryParam && parsId === undefined) {
			getVersionsQuery(queryParam, page, items);
		}
		 
	}, [typeParam, queryParam, parsId, page, items]);

	return {
		data: data || (EMPTY_LIST as IFeedback[] as T),
		setData: setData as stateSetter<T>,
		getFeedbacks,
		loading,
		loadingCTA,
		tableConfig,
		loadingTableConfig,
		editFeedback,
		removeFeedback,
		items,
		page,
		setItems,
		setPage,
		totalItems,
		totalPages
	};
}
